import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../../config/api';

const SystemConfiguration: React.FC = () => {
  const navigate = useNavigate();
  
  const [settings, setSettings] = useState({
    allowSelfRegistration: true,
    requireManagerApproval: true,
    requireFinanceApproval: true,
    maxRequestAmount: 10000,
    emailNotifications: true,
    autoApprovalLimit: 500,
  });

  const [departments, setDepartments] = useState<string[]>([]);
  const [newDepartment, setNewDepartment] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/users/departments`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch departments');
      }

      const data = await response.json();
      setDepartments(data.map((dept: any) => dept.name));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load departments');
      // Fallback to default departments
      setDepartments(['IT', 'Marketing', 'Sales', 'Finance', 'HR', 'Operations']);
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (setting: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value,
    }));
  };

  const handleAddDepartment = async () => {
    if (newDepartment.trim() && !departments.includes(newDepartment.trim())) {
      const updatedDepartments = [...departments, newDepartment.trim()];
      setDepartments(updatedDepartments);
      setNewDepartment('');
      
      // Save to backend immediately
      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/users/departments`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ departments: updatedDepartments }),
        });

        if (!response.ok) {
          throw new Error('Failed to save department');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save department');
        // Revert the local state if save failed
        setDepartments(departments);
      }
    }
  };

  const handleDeleteDepartment = async (dept: string) => {
    const updatedDepartments = departments.filter(d => d !== dept);
    setDepartments(updatedDepartments);
    
    // Save to backend immediately
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/users/departments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ departments: updatedDepartments }),
      });

      if (!response.ok) {
        throw new Error('Failed to save department changes');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save department changes');
      // Revert the local state if save failed
      setDepartments(departments);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);

      // Save departments first 
      const departmentResponse = await fetch(`${API_CONFIG.BASE_URL}/users/departments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ departments }),
      });

      if (!departmentResponse.ok) {
        throw new Error('Failed to save departments');
      }

      // TODO: Save other settings when backend endpoint is available
      console.log('Saving settings:', settings);
      alert('Departments saved successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, py: 4 }}>
      <Container maxWidth="lg">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item>
                  <IconButton onClick={() => navigate('/dashboard')} size="large">
                    <ArrowBackIcon />
                  </IconButton>
                </Grid>
                <Grid item xs>
                  <Typography variant="h4" gutterBottom>
                    System Configuration
                  </Typography>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveSettings}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Settings'}
                  </Button>
                </Grid>
              </Grid>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid container spacing={3}>
                {/* General Settings */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        General Settings
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.allowSelfRegistration}
                              onChange={(e) => handleSettingChange('allowSelfRegistration', e.target.checked)}
                            />
                          }
                          label="Allow Self Registration"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.emailNotifications}
                              onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                            />
                          }
                          label="Email Notifications"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Approval Settings */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Approval Settings
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.requireManagerApproval}
                              onChange={(e) => handleSettingChange('requireManagerApproval', e.target.checked)}
                            />
                          }
                          label="Require Manager Approval"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.requireFinanceApproval}
                              onChange={(e) => handleSettingChange('requireFinanceApproval', e.target.checked)}
                            />
                          }
                          label="Require Finance Approval"
                        />
                        <TextField
                          fullWidth
                          label="Auto Approval Limit ($)"
                          type="number"
                          value={settings.autoApprovalLimit}
                          onChange={(e) => handleSettingChange('autoApprovalLimit', Number(e.target.value))}
                        />
                        <TextField
                          fullWidth
                          label="Maximum Request Amount ($)"
                          type="number"
                          value={settings.maxRequestAmount}
                          onChange={(e) => handleSettingChange('maxRequestAmount', Number(e.target.value))}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Department Management */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Department Management
                      </Typography>
                      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Grid item xs>
                          <TextField
                            fullWidth
                            label="New Department"
                            value={newDepartment}
                            onChange={(e) => setNewDepartment(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddDepartment()}
                          />
                        </Grid>
                        <Grid item>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddDepartment}
                          >
                            Add
                          </Button>
                        </Grid>
                      </Grid>
                      <Divider sx={{ my: 2 }} />
                      <List>
                        {departments.map((dept, index) => (
                          <ListItem key={index}>
                            <ListItemText primary={dept} />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                color="error"
                                onClick={() => handleDeleteDepartment(dept)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default SystemConfiguration; 